/* eslint-disable react/no-danger */
import * as React from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { breakpoints } from '@styles/index';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getIsLoggedIn } from '@selectors/auth';
import H5 from '@base/H5/H5';
import Button from '@base/Button/Button';
import HTMLWrapper from '@comp/Question/HTMLWrapper';
import { updateFreePracticeQuestionCount } from '@actions/freePracticeSession';
import Option from '@comp/Question/Fields/Option';
import getContent from './helpers/getContent';

const Answers = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  margin: 24px 0 32px 0;
  & .correct {
    border: 1px solid var(--color-i-green);
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Submit = styled.div`
  display: flex;
  justify-content: flex-end;
  &.multiple {
    justify-content: space-between;
  }
  & button {
    font-size: 14px;
    height: 40px;
    min-height: 40px;
    width: 104px;
  }
`;

const Solution = styled.div`
  margin-bottom: 32px;
`;

const SolutionTitle = styled(H5)`
  font-weight: 400;
  color: var(--color-text-primary);
  margin-bottom: 8px;
`;

const Div = styled(HTMLWrapper)`
  & img {
    filter: invert(var(--filter));
  }
  & * {
    color: var(--color-text-primary);
  }
`;

const Question = ({
  question,
  nextQuestion,
  isLast,
  finishTest,
  currentQuestion,
  previousQuestion,
  attemptedQuestion,
  updateScore
}) => {
  const router = useRouter();
  const { goalUID } = router.query;

  const [submitted, setSubmitted] = React.useState(false);
  const [selected, setSelected] = React.useState('');
  const showPreviousButton = (isLast && submitted) || currentQuestion === 1;

  const dispatch = useDispatch();
  const isLoggedIn = useSelectorWithShallowEqual(getIsLoggedIn);

  const handleSubmit = () => {
    if (submitted) {
      nextQuestion();
      setSubmitted(false);
    } else {
      updateScore({
        isCorrect: selected === question.correct_answer,
        selected
      });
      setSubmitted(true);
      if (isLoggedIn) {
        dispatch(updateFreePracticeQuestionCount({ goalUID }));
      }
    }
  };
  React.useEffect(() => {
    if (attemptedQuestion.selected) {
      setSubmitted(true);
      setSelected(attemptedQuestion.selected);
    } else {
      setSubmitted(false);
      setSelected('');
    }
  }, [attemptedQuestion.selected, question]);
  return (
    <>
      <Div
        dangerouslySetInnerHTML={{
          __html: getContent(question.content)
        }}
      />
      <Answers>
        {question.answers.map((answer, index) => {
          const isInCorrect =
            submitted &&
            selected !== question.correct_answer &&
            selected === answer.uid;
          const isCorrect =
            submitted &&
            ((selected === question.correct_answer &&
              selected === answer.uid) ||
              answer.uid === question.correct_answer);
          return (
            <Option
              uid={answer.uid}
              key={answer.uid}
              isSelectable={!submitted}
              isCorrect={isCorrect}
              isInCorrect={isInCorrect}
              isSelected={selected === answer.uid}
              label={String.fromCharCode(65 + index)}
              innerHTML={{
                __html: getContent(answer.content)
              }}
              onOptionClick={(value) => {
                setSelected(value);
              }}
              className={isCorrect ? 'correct' : ''}
            />
          );
        })}
      </Answers>
      {submitted && (
        <Solution>
          <SolutionTitle>Solution</SolutionTitle>
          <Div
            dangerouslySetInnerHTML={{
              __html: getContent(question.solution_explanation)
            }}
          />
        </Solution>
      )}
      <Submit className={showPreviousButton ? '' : 'multiple'}>
        {showPreviousButton ? null : (
          <Button
            label="Previous"
            variant="hollow"
            color="black"
            onClick={previousQuestion}
          />
        )}
        {isLast && submitted ? (
          <Button
            label="Finish"
            color="black"
            onClick={finishTest}
            variant="filled"
          />
        ) : (
          <Button
            label={submitted ? 'Next' : 'Submit'}
            color="black"
            onClick={handleSubmit}
            variant={!selected ? 'disabled' : 'filled'}
          />
        )}
      </Submit>
    </>
  );
};

Question.propTypes = {
  question: PropTypes.objectOf(PropTypes.any).isRequired,
  nextQuestion: PropTypes.func.isRequired,
  previousQuestion: PropTypes.func.isRequired,
  updateScore: PropTypes.func.isRequired,
  finishTest: PropTypes.func.isRequired,
  isLast: PropTypes.bool.isRequired,
  currentQuestion: PropTypes.number.isRequired,
  attemptedQuestion: PropTypes.objectOf(PropTypes.any).isRequired
};

export default Question;
